import React, { useCallback, useEffect, useState } from "react"

import { detect } from "detect-browser"

import { Subscription } from "@rails/actioncable"

import useWebsocket from "hooks/useWebsocket"

import useGeminiLiveTranslation from "../ChimeMeetingHooks/useGeminiLiveTranslation"
import usePlaybackRate from "../ChimeMeetingHooks/usePlaybackRate"
import useTranscriptForwarding from "../ChimeMeetingHooks/useTranscriptForwarding"

import useAttendeeInfo from "./hooks/useAttendeeInfo"
import useChimeCloudroom from "./hooks/useChimeCloudroom"
import useCloudroomSessionConfig from "./hooks/useCloudroomSessionConfig"
import useLocalAVControls from "./hooks/useLocalAVControls"
import useRemoteAVControls from "./hooks/useRemoteAVControls"
import useRoster from "./hooks/useRoster"
import useSurveyModal from "./hooks/useSurveyModal"

import { ErrorType } from "./Cloudroom"
import CloudroomContext from "./CloudroomContext"

export type CloudroomProviderProps = {
  session: any
  localUser: {
    type: "Student" | "Tutor" | "Teacher" | "Admin"
    id: string
  }
  ghost?: boolean
  children: React.ReactNode
}

const CloudroomProvider: React.FC<CloudroomProviderProps> = ({
  session,
  localUser,
  ghost = false,
  children,
}) => {
  const sessionId = session.id

  const [error, setError] = useState<ErrorType | null>(null)
  const clearError = () => setError(null)
  const handleError = useCallback((error: ErrorType) => {
    const browser = detect()
    if (browser?.os === "iOS" && browser?.name !== "ios") {
      alert("For iOS support, please use the Safari browser")
    } else {
      setError(error)
    }
  }, [])

  const { channel }: { channel: Subscription } = useWebsocket({
    channelName: "CloudroomChannel",
    channelProps: { id: sessionId, ghost },
  })

  useWebsocket({
    channelName: "CloudroomUserChannel",
    channelProps: { id: sessionId },
  })

  const { roster, rosterHandlers } = useRoster()
  const { sessionConfig } = useCloudroomSessionConfig(sessionId)
  const { surveyOpen, setSurveyOpen } = useSurveyModal()
  const { attendeesInfo, getInfoForExternalUserId } = useAttendeeInfo()

  const {
    audioVideo,
    audioSetupInProgress,
    videoSetupInProgress,
    configureMicrophone,
  } = useChimeCloudroom({
    session,
    localUser,
    rosterHandlers,
    ghost,
    handleError,
  })

  const localControls = useLocalAVControls({
    audioVideo,
    configureMicrophone,
    lowBandwidthStudent:
      session.lowBandwidthCloudroom && localUser.type === "Student",
    handleError,
  })

  const remoteControls = useRemoteAVControls({
    audioVideo,
    channel,
    session,
    localUser,
    ...localControls,
  })

  useTranscriptForwarding({
    enabled: sessionConfig?.persistSessionTranscript,
    audioVideo,
    userId: localUser.id,
    channel,
  })

  const [activeLanguageCode, setActiveLanguageCode] = useState<string | null>(
    null
  )
  const [playbackRate, setPlaybackRate] = useState(1)

  const localAttendeeInfo = attendeesInfo[localUser.id]
  useEffect(() => {
    if (localUser.type !== "Student" || !localAttendeeInfo) return

    const updatedLanguageCode = localAttendeeInfo.liveTranslationEnabled
      ? localAttendeeInfo.defaultLiveTranslationLanguageCode
      : null
    setActiveLanguageCode(updatedLanguageCode)
    setPlaybackRate(localAttendeeInfo.audioPlaybackRate)
  }, [localUser.type, localAttendeeInfo])

  const tutorAttendeeId = Object.values(roster).find(attendee =>
    attendee.externalUserId.startsWith("Tutor-")
  )?.attendeeId

  usePlaybackRate({
    audioVideo,
    playbackRate,
    instructorAttendeeId: tutorAttendeeId,
  })

  const tutorShouldTranslateAudio = Object.values(attendeesInfo).some(
    info => info.liveTranslationEnabled
  )

  const targetLanguageCode =
    localUser.type === "Tutor" && tutorShouldTranslateAudio
      ? "en"
      : activeLanguageCode
  useGeminiLiveTranslation({
    targetLanguageCode,
    audioVideo,
    chimeMeetingId: session.chimeJoinInfo?.meeting?.meeting_id,
  })

  return (
    <CloudroomContext.Provider
      value={{
        session,
        ghost,
        audioVideo,
        audioSetupInProgress,
        videoSetupInProgress,
        ...localControls,
        ...remoteControls,
        channel,
        sessionConfig,
        roster,
        localUser,
        error,
        clearError,
        surveyOpen,
        setSurveyOpen,
        activeLanguageCode,
        setActiveLanguageCode,
        playbackRate,
        setPlaybackRate,
        getInfoForExternalUserId,
      }}
    >
      {children}
    </CloudroomContext.Provider>
  )
}

export default CloudroomProvider
